import React, { useState, useEffect } from 'react';
import { Check, ArrowRight, Users, HeartPulse, Dumbbell, Music, Layers, Sparkles, Flower2, Smile, Car } from 'lucide-react';
import { Button } from './ui/button';
import { useParams, Link, Navigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import Reveal from './Reveal';
import { useContent } from '../context/ContentContext';
import { useLang } from '../context/LanguageContext';
import PilotRequestModal from './PilotRequestModal';

const ICONS = { care: HeartPulse, fit: Dumbbell, dance: Music, beauty: Flower2, dental: Smile, auto: Car };
export const PRODUCT_PATHS = { care: '/invictacare', fit: '/invictafit', dance: '/invictadance', beauty: '/invictabeauty', dental: '/invictadental', auto: '/invictaauto' };

const FeatureGrid = ({ features, color }) => (
  <div className="grid sm:grid-cols-2 gap-x-10 gap-y-5">
    {(features || []).map((f, i) => (
      <div key={i} className="flex items-start gap-3">
        <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color }} strokeWidth={2.5} />
        <div>
          <p className="text-[#1D1D1F] font-medium">{f.text}</p>
          {f.desc && <p className="text-sm text-[#86868B] mt-0.5 leading-relaxed">{f.desc}</p>}
        </div>
      </div>
    ))}
  </div>
);

export default function ProductPage({ productId }) {
  const params = useParams();
  const id = productId || params.productId;
  const data = useContent('products', null);
  const { t } = useLang();
  const [showPilot, setShowPilot] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (data && !(data.items || []).some((i) => i.id === id)) {
    return <Navigate to="/" replace />;
  }
  if (!data) return null;

  const item = data.items.find((i) => i.id === id);
  const color = item.color || '#0071E3';
  const Icon = ICONS[id] || HeartPulse;
  const ctaLive = (data.cta_live || 'Zu {name}').replace('{name}', item.name);
  const note = data.platform_note || {};
  const details = data.platform_details || {};
  const offer = data.founders_offer || {};
  const others = data.items.filter((i) => i.id !== id);
  const detailSections = item.detail_sections || [];
  const sharedSections = data.shared_sections || [];
  const seoLayout = !!item.seo_layout;

  const allFeatureTexts = [
    ...(item.features || []).map((f) => f.text),
    ...detailSections.flatMap((s) => (s.features || []).map((f) => f.text)),
    ...sharedSections.flatMap((s) => (s.features || []).map((f) => f.text)),
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: item.name,
    description: `${item.claim}. ${item.audience}`,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: item.subdomain_live ? item.url : `https://www.invictaflow.ch${PRODUCT_PATHS[id]}`,
    featureList: allFeatureTexts,
    publisher: { '@type': 'Organization', name: 'InvictaX Group GmbH', url: 'https://www.invictaflow.ch' },
  };

  const faqJsonLd = (item.faq || []).length > 0
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: item.faq.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      }
    : null;

  const CtaButton = ({ className = '', variant = 'hero' }) =>
    item.subdomain_live ? (
      <a href={item.url} target="_blank" rel="noopener noreferrer">
        <Button
          className={`rounded-full h-12 px-8 text-white hover:opacity-90 group ${className}`}
          style={{ backgroundColor: color }}
          data-testid={`product-page-cta-${id}-${variant}`}
        >
          {ctaLive}
          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Button>
      </a>
    ) : (
      <Button
        className={`rounded-full h-12 px-8 text-white hover:opacity-90 ${className}`}
        style={{ backgroundColor: color }}
        onClick={() => setShowPilot(true)}
        data-testid={`product-page-cta-${id}-${variant}`}
      >
        {data.cta_soon || 'Bald verfügbar – Pilotkunde werden'}
      </Button>
    );

  const offerBlock = offer.enabled ? (
    <section className="px-6 pt-14" data-testid={`founders-offer-${id}`}>
      <div className="max-w-[980px] mx-auto">
        <Reveal>
          <div className="bg-[#1D1D1F] rounded-3xl p-8 md:p-12 grid md:grid-cols-[1fr_auto] gap-10 items-center overflow-hidden relative">
            <div
              className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-20 blur-3xl"
              style={{ backgroundColor: color }}
            />
            <div className="relative">
              <span
                className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-white rounded-full px-3 py-1.5 mb-5"
                style={{ backgroundColor: color }}
              >
                <Sparkles className="w-3.5 h-3.5" /> {offer.badge}
              </span>
              <h2 className="text-2xl md:text-3xl font-semibold text-white tracking-tight mb-3">
                {offer.title}
              </h2>
              <p className="text-[#A1A1A6] leading-relaxed max-w-xl mb-4">{offer.text}</p>
              <p className="text-sm font-medium text-white/90 flex items-center gap-2">
                <Check className="w-4 h-4" style={{ color }} strokeWidth={2.5} />
                {offer.limit_note}
              </p>
            </div>
            <div className="relative text-center md:text-right md:min-w-[260px]">
              <p className="text-5xl font-semibold text-white tracking-tight">{offer.price}</p>
              <p className="text-sm text-[#A1A1A6] mt-1">{offer.price_period}</p>
              <p className="text-sm text-[#A1A1A6]">{offer.after_price}</p>
              <Button
                className="rounded-full h-12 px-8 mt-6 text-white hover:opacity-90"
                style={{ backgroundColor: color }}
                onClick={() => setShowPilot(true)}
                data-testid={`founders-offer-cta-${id}`}
              >
                {offer.cta}
              </Button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  ) : null;

  return (
    <>
      <Header />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      {faqJsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      )}
      <main className="bg-white" data-testid={`product-page-${id}`}>
        <section className="pt-36 pb-16 px-6" style={{ backgroundColor: `${color}0D` }}>
          <div className="max-w-[860px] mx-auto text-center">
            {item.logo ? (
              <img
                src={item.logo}
                alt={`${item.name} Logo`}
                className="h-24 w-auto max-w-[200px] mx-auto mb-6 object-contain"
                data-testid={`product-logo-${id}`}
              />
            ) : (
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
                style={{ backgroundColor: `${color}1A` }}
              >
                <Icon className="w-8 h-8" style={{ color }} strokeWidth={1.8} />
              </div>
            )}
            <h1 className="text-4xl sm:text-5xl font-semibold text-[#1D1D1F] tracking-tight mb-4">
              {item.h1 || item.name}
            </h1>
            <p className="text-base md:text-lg text-[#424245] font-medium mb-4">{item.hero_subtitle || item.claim}</p>
            <div className="flex items-center justify-center gap-2 text-[#86868B] mb-6">
              <Users className="w-4 h-4" strokeWidth={1.5} />
              <p className="text-sm">
                <span className="font-semibold text-[#1D1D1F]">{t('platforms.audiencePrefix')} </span>
                {item.audience}
              </p>
            </div>
            {item.price_from && (
              <p className="text-base font-semibold mb-6" style={{ color }} data-testid={`product-page-price-${id}`}>
                {item.price_from}
              </p>
            )}
            <CtaButton />
          </div>
        </section>

        {item.problems && (
          <section className="py-16 px-6" data-testid={`product-problems-${id}`}>
            <div className="max-w-[860px] mx-auto">
              <Reveal>
                <h2 className="text-3xl font-semibold text-[#1D1D1F] tracking-tight mb-4 text-center">
                  {item.problems.title}
                </h2>
                <p className="text-[#424245] leading-relaxed max-w-2xl mx-auto text-center mb-10">
                  {item.problems.intro}
                </p>
              </Reveal>
              <div className="grid sm:grid-cols-2 gap-6">
                {(item.problems.items || []).map((p, i) => (
                  <Reveal key={i} delay={i * 50}>
                    <div className="bg-[#F5F5F7] rounded-3xl p-7 h-full">
                      <h3 className="text-base font-semibold text-[#1D1D1F] mb-2">{p.text}</h3>
                      <p className="text-sm text-[#515154] leading-relaxed">{p.desc}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}

        {!seoLayout && offerBlock}

        {!seoLayout && (
        <section className="py-16 px-6">
          <div className="max-w-[860px] mx-auto">
            <Reveal>
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-8 text-center">
                {t('projects.functions')}
              </h2>
              <div className="grid sm:grid-cols-2 gap-x-10 gap-y-4 mb-4">
                {(item.features || []).map((f, i) => (
                  <div key={i} className="flex items-start gap-3 text-[#424245]">
                    <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color }} strokeWidth={2.5} />
                    <span>
                      {f.text}
                      {f.coming_soon && (
                        <span className="ml-2 inline-block align-middle text-[10px] font-semibold uppercase tracking-wide bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full">
                          {data.badge_soon || 'Bald verfügbar'}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
        )}

        {detailSections.length > 0 && seoLayout && (
          <section className="py-16 px-6 bg-[#F5F5F7]" data-testid={`product-details-${id}`}>
            <div className="max-w-[860px] mx-auto space-y-10">
              {detailSections.map((section, si) => (
                <Reveal key={si}>
                  <div className="bg-white rounded-3xl p-8 md:p-10 shadow-sm">
                    <h2 className="text-2xl font-semibold tracking-tight mb-3" style={{ color }}>
                      {section.title}
                    </h2>
                    {section.intro && (
                      <p className="text-[#424245] leading-relaxed mb-7">{section.intro}</p>
                    )}
                    <FeatureGrid features={section.features} color={color} />
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {seoLayout && offerBlock}

        {seoLayout && (item.faq || []).length > 0 && (
          <section className="py-16 px-6" data-testid={`product-faq-${id}`}>
            <div className="max-w-[860px] mx-auto">
              <Reveal>
                <h2 className="text-3xl font-semibold text-[#1D1D1F] tracking-tight mb-10 text-center">
                  {item.faq_title}
                </h2>
              </Reveal>
              <div className="space-y-4">
                {item.faq.map((f, i) => (
                  <Reveal key={i}>
                    <div className="bg-[#F5F5F7] rounded-2xl p-6" data-testid={`product-faq-item-${id}-${i}`}>
                      <h3 className="text-base font-semibold text-[#1D1D1F] mb-2">{f.q}</h3>
                      <p className="text-sm text-[#515154] leading-relaxed">{f.a}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}

        {seoLayout && item.platform_summary && (
          <section className="py-16 px-6 bg-[#F5F5F7]" data-testid={`product-platform-summary-${id}`}>
            <div className="max-w-[860px] mx-auto">
              <Reveal>
                <h2 className="text-3xl font-semibold text-[#1D1D1F] tracking-tight mb-4 text-center">
                  {item.platform_summary.title}
                </h2>
                <p className="text-[#424245] leading-relaxed max-w-2xl mx-auto text-center mb-8">
                  {item.platform_summary.text}
                </p>
                <div className="flex flex-wrap justify-center gap-2 mb-10">
                  {(item.platform_summary.modules || []).map((m, i) => (
                    <span key={i} className="text-sm text-[#1D1D1F] bg-white rounded-full px-4 py-1.5 shadow-sm">
                      {m}
                    </span>
                  ))}
                </div>
              </Reveal>
              <div className="grid sm:grid-cols-2 gap-6 mb-10">
                {(item.platform_summary.cards || []).map((c, i) => (
                  <Reveal key={i} delay={i * 50}>
                    <div className="bg-white rounded-3xl p-7 h-full shadow-sm">
                      <h3 className="text-base font-semibold text-[#1D1D1F] mb-2">{c.title}</h3>
                      <p className="text-sm text-[#86868B] leading-relaxed">{c.text}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
              <div className="text-center">
                <Link
                  to="/plattformen"
                  className="inline-flex items-center gap-2 font-medium hover:underline"
                  style={{ color }}
                  data-testid={`platform-summary-link-${id}`}
                >
                  {item.platform_summary.link_label}
                  <ArrowRight className="w-4 h-4" />
                </Link>
                {(item.platform_summary.cross_links || []).length > 0 && (
                  <div className="mt-6 flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm">
                    {item.platform_summary.cross_links.map((l, i) => (
                      <Link key={i} to={l.to} className="text-[#515154] hover:underline" data-testid={`cross-link-${id}-${i}`}>
                        {l.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {detailSections.length > 0 && !seoLayout && (
          <section className="py-16 px-6 bg-[#F5F5F7]" data-testid={`product-details-${id}`}>
            <div className="max-w-[860px] mx-auto">
              <Reveal>
                <h2 className="text-3xl font-semibold text-[#1D1D1F] mb-12 text-center tracking-tight">
                  {data.detail_title || 'Alle Funktionen im Detail'}
                </h2>
              </Reveal>
              <div className="space-y-10">
                {detailSections.map((section, si) => (
                  <Reveal key={si}>
                    <div className="bg-white rounded-3xl p-8 md:p-10 shadow-sm">
                      <h3 className="text-xl font-semibold mb-6" style={{ color }}>
                        {section.title}
                      </h3>
                      <FeatureGrid features={section.features} color={color} />
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}

        {!seoLayout && sharedSections.length > 0 && (
          <section className="py-16 px-6" data-testid={`product-shared-${id}`}>
            <div className="max-w-[860px] mx-auto">
              <Reveal>
                <div className="text-center mb-12">
                  <div className="w-12 h-12 rounded-2xl bg-[#F5F5F7] flex items-center justify-center mx-auto mb-5">
                    <Layers className="w-6 h-6 text-[#1D1D1F]" strokeWidth={1.8} />
                  </div>
                  <h2 className="text-3xl font-semibold text-[#1D1D1F] mb-3 tracking-tight">
                    {data.shared_title}
                  </h2>
                  <p className="text-[#86868B] max-w-2xl mx-auto">{data.shared_subtitle}</p>
                </div>
              </Reveal>
              <div className="space-y-12">
                {sharedSections.map((section, si) => (
                  <Reveal key={si}>
                    <div>
                      <h3 className="text-lg font-semibold text-[#1D1D1F] mb-5 pb-3 border-b border-[#E8E8ED]">
                        {section.title}
                      </h3>
                      <FeatureGrid features={section.features} color={color} />
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}

        {!seoLayout && (details.items || []).length > 0 && (
          <section className="py-16 px-6 bg-[#F5F5F7]" data-testid={`product-platform-${id}`}>
            <div className="max-w-[980px] mx-auto">
              <Reveal>
                <h2 className="text-3xl font-semibold text-[#1D1D1F] mb-12 text-center tracking-tight">
                  {details.title}
                </h2>
              </Reveal>
              <div className="grid md:grid-cols-2 gap-6">
                {details.items.map((d, i) => (
                  <Reveal key={i} delay={i * 50}>
                    <div className="bg-white rounded-3xl p-7 h-full shadow-sm">
                      <h3 className="text-base font-semibold text-[#1D1D1F] mb-2">{d.title}</h3>
                      <p className="text-sm text-[#86868B] leading-relaxed">{d.text}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-16 px-6">
          <div className="max-w-[860px] mx-auto">
            {note.title && (
              <Reveal>
                <div className="bg-[#1D1D1F] rounded-3xl p-8 md:p-10 text-center mb-16">
                  <h3 className="text-xl font-semibold text-white mb-3">{note.title}</h3>
                  <p className="text-[#A1A1A6] max-w-2xl mx-auto mb-5">{note.text}</p>
                  <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 mb-7">
                    {(note.points || []).map((point, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-white">
                        <Check className="w-4 h-4 text-[#2997FF]" strokeWidth={2} />
                        <span>{point}</span>
                      </div>
                    ))}
                  </div>
                  <CtaButton variant="note" />
                </div>
              </Reveal>
            )}

            <div className="text-center">
              <p className="text-xs font-semibold text-[#86868B] tracking-widest uppercase mb-4">
                {t('productPage.other')}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                {others.map((o) => (
                  <Link
                    key={o.id}
                    to={PRODUCT_PATHS[o.id] || '/'}
                    className="inline-flex items-center gap-2 text-[#1D1D1F] hover:underline font-medium"
                    data-testid={`product-other-link-${o.id}`}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: o.color }} />
                    {o.name}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {showPilot && (
        <PilotRequestModal products={data.items} preselect={id} onClose={() => setShowPilot(false)} />
      )}
    </>
  );
}
